<template>
  <article
    v-if="showHeader || mainElementMainImage || showSlider || mainElementVideo"
    :key="keyid"
    v-observe-visibility="{
      callback: handleScrollIntoView,
      once: true,
    }"
    :data-key="keyid"
    :data-banner-type="bannerType"
    :class="bemBlockMod(bannerClass)"
  >
    <BaseTitleSubtitle
      v-if="showHeader"
      :class="bemElement('title-subtitle')"
      :title="product.banner.mainElement.title"
      :subtitle="product.banner.mainElement.subtitle"
      :color="product.banner.mainElement.color"
    />

    <BaseLink
      v-if="mainElementMainImage || mainElementVideo"
      v-bind="product.banner.mainElement.link"
      :class="bemElement('link')"
      @click="mainElementClick()"
    >
      <BaseVimeoVideo
        v-if="mainElementVideo"
        :class="bemElement('image')"
        :video-desktop="mainElementVideo"
        :video-mobile="mainElementVideo"
        :video-poster-mobile="mainElementMainImage[300].url"
        :video-poster-desktop="mainElementMainImage[1200].url"
        :video-autoplay="true"
        :video-muted="true"
        :video-loop="true"
      />

      <BaseImage
        v-else
        :class="bemElement('pic')"
        :data-url="product.banner.mainElement.link.url"
        :image-classes="bemElement('image')"
        :src="getDefaultImageUrlFromProductImageSet(mainElementMainImage)"
        :alt="getDefaultImageAltFromProductImageSet(mainElementMainImage)"
        :loading="forceMainImageToLoad ? 'eager' : null"
        :placeholder="
          getPlaceholderImageUrlFromProductImageSet(mainElementMainImage)
        "
        :sizes="getSizes()"
        :srcset="getSrcsetFromProductImageSet(mainElementMainImage)"
        :use-title="false"
      />
    </BaseLink>

    <BaseCarousel
      v-if="showSlider"
      :class="bemElement('slider')"
      :are-controls-shown="false"
      :items="sliderItems"
      :swiper-options="{
        allowTouchMove: true,
        loop: false,
        slidesPerView: 2.5,
        spaceBetween: 2,
      }"
    >
      <template #default="slotProps">
        <!-- Mobile / web mobile: render directly from the minimal payload (no lazy fetch) -->
        <BaseProductTile
          v-if="isBreakpointMobile"
          :analytics="{
            location: analyticsConst.location.plpSliderBanner,
            position: gridIndex + (slotProps.index + 1) / 10,
          }"
          :are-carousel-controls-shown="false"
          :carousel-index="slotProps.index"
          :context="context"
          :force-main-image-to-load="true"
          :grid-index="gridIndex"
          :grid-type="gridType"
          :has-carousel="false"
          :hide-prices="hidePrices"
          :hide-product-names="hideProductNames"
          :hide-tags="hideTags"
          :keyid="`${slotProps.item.id}-${slotProps.index}`"
          :minimal="minimal"
          :product="slotProps.item"
          :show-swatches="false"
          :url="slotProps.item.selectedProductUrl || ''"
        />
        <!-- Desktop skeleton: show the lightweight image until the full tile is fetched -->
        <BaseProductTileImage
          v-else-if="loadedProducts === null"
          :has-carousel="false"
          :url="''"
          :context="context"
          :force-main-image-to-load="true"
          :product="toSliderSkeletonProduct(slotProps.item)"
        />
        <!-- Desktop loaded: render the full product tile as a normal grid tile
             (with its own images and per-tile carousel), per SF-11727/SF-11428 -->
        <BaseProductTile
          v-else
          :analytics="{
            location: analyticsConst.location.plpSliderBanner,
            position: gridIndex + (slotProps.index + 1) / 10,
          }"
          :are-carousel-controls-shown="false"
          :context="context"
          :force-main-image-to-load="true"
          :grid-index="gridIndex"
          :grid-type="gridType"
          :has-carousel="true"
          :hide-prices="hidePrices"
          :hide-product-names="hideProductNames"
          :hide-tags="hideTags"
          :keyid="`${slotProps.item.id}-${slotProps.index}`"
          :minimal="minimal"
          :product="slotProps.item"
          :show-swatches="false"
          :url="slotProps.item.selectedProductUrl || ''"
        />
      </template>
    </BaseCarousel>
  </article>
</template>

<script>
import { paramCase } from 'change-case';
import bemClassesMixin from '../mixins/bemClassesMixin';
import { productImageSetMixin } from '../mixins/productImageSetMixin';
import productPropBaseMixin from '../mixins/productPropBaseMixin';
import BaseCarousel from './BaseCarousel.vue';
import BaseImage from './BaseImage.vue';
import BaseLink from './BaseLink.vue';
import BaseVimeoVideo from './BaseVimeoVideo.vue';
import BaseProductTile from './BaseProductTile.vue';
import BaseProductTileImage from './BaseProductTileImage.vue';
import BaseTitleSubtitle from './BaseTitleSubtitle.vue';
import getAnalyticsEventBus from '../../common/getAnalyticsEventBus';
import getFrontConfig from '../../common/getFrontConfig';
import { types } from '../store';
import {
  isSliderBannerEnriched,
  isSliderBannerUnavailable,
  toSliderSkeletonProduct,
} from '../utils/sliderBannerProducts';
import analyticsMixin from '../mixins/analyticsMixin';
import checkVisibilityMixin from '../mixins/checkVisibilityMixin';
import windowResizeMixin from '../mixins/windowResizeMixin';

const analyticsEventBus = getAnalyticsEventBus();

const {
  productImages: { imageSets },
  responsive: { bp_768: bp768 },
  vue: {
    BaseProductTile: {
      context: { plpSliderBanner },
    },
    TheProductList: { bannerTypes },
  },
} = getFrontConfig();

const BANNER_TYPE_TO_ANALYTICS = {
  [bannerTypes.plpMobileSlider]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerCarouselViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerCarouselViewed.type
          .bannerCarousel,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerCarousel,
    },
  },
  [bannerTypes.plpFullImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageFull,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageFull,
    },
  },
  [bannerTypes.plpDoubleImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageDouble,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageDouble,
    },
  },
  [bannerTypes.plpSimpleImage]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerImageSimple,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerImageSimple,
    },
  },
  [bannerTypes.plpSimpleVideo]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerVideoSimple,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerVideoSimple,
    },
  },
  [bannerTypes.plpDoubleVideo]: {
    viewed: {
      eventName: analyticsEventBus.EVENTS.productListBannerViewed,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerViewed.type
          .bannerVideoDouble,
    },
    clicked: {
      eventName: analyticsEventBus.EVENTS.productListBannerClicked,
      payloadType:
        analyticsEventBus.EVENTS.PAYLOADS.productListBannerClicked.type
          .bannerVideoDouble,
    },
  },
};

const SIZES = {
  PLP_GRID: '(min-width: 768px) 25vw, 50vw',
  PLP_DOUBLE_WIDTH: '100vw',
};

export default {
  name: 'TheGridBanner',

  components: {
    BaseCarousel,
    BaseImage,
    BaseLink,
    BaseProductTile,
    BaseProductTileImage,
    BaseTitleSubtitle,
    BaseVimeoVideo,
  },

  mixins: [
    analyticsMixin,
    bemClassesMixin,
    checkVisibilityMixin,
    productPropBaseMixin,
    productImageSetMixin,
    windowResizeMixin,
  ],

  props: {
    context: {
      type: String,
      required: false,
      default: plpSliderBanner,
    },
    forceMainImageToLoad: {
      default: false,
      required: false,
      type: Boolean,
    },
    gridIndex: {
      type: Number,
      required: true,
    },
    gridType: {
      type: String,
      validator: (prop) =>
        ['crossSelling', 'productList', 'search'].includes(prop),
      required: false,
      default: null,
    },
    hidePrices: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideProductNames: {
      type: Boolean,
      default: false,
      required: false,
    },
    hideTags: {
      type: Boolean,
      default: false,
      required: false,
    },
    keyid: {
      type: String,
      default: '',
      required: false,
    },
    minimal: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data: () => ({
    dataChangeBreakpoint: bp768,
    videoBlurFix: false,
    /** Tracks whether the banner has entered the viewport (for resize → desktop fetch) */
    hasBeenVisible: false,
  }),

  computed: {
    bannerClass() {
      const listClass = {
        minimal: this.minimal,
        'video-blur-fix': this.videoBlurFix,
      };
      const type = paramCase(this.bannerType);
      listClass[type] = true;
      return listClass;
    },
    bannerType() {
      return this.product.banner.type;
    },
    mainElementImageSet() {
      let imageSet = imageSets.PLP;
      if (this.context === plpSliderBanner) {
        imageSet = imageSets.PLAIN;
      }
      const bylImages =
        this.isBreakpointDesktop &&
        this.product.banner.mainElement.bylImagesDesktop
          ? this.product.banner.mainElement.bylImagesDesktop
          : this.product.banner.mainElement.bylImages;
      return this.getProductImageSets(imageSet, bylImages);
    },
    mainElementMainImage() {
      return this.mainElementImageSet?.[0];
    },
    mainElementVideo() {
      const { videoUrl } = this.product.banner.mainElement;
      return videoUrl ? this.decodeHtml(videoUrl) : null;
    },
    /**
     * Minimal array from initial payload: [{id, title, imageUrl, available, bylComingSoon, price, url}]
     * @returns {Object[]} the minimal slider payload
     */
    productSliderIds() {
      return this.product.banner.productSlider || [];
    },
    /**
     * Maps the minimal payload to the shape BaseProductTile consumes (mobile renders directly)
     * @returns {Object[]} tile-shaped slider items
     */
    mobileSliderItems() {
      return this.productSliderIds.map((item) => ({
        id: item.id,
        productName: item.title,
        image: { url: item.imageUrl, alt: item.title },
        price: item.price,
        available: item.available,
        bylComingSoon: item.bylComingSoon,
        selectedProductUrl: item.url,
      }));
    },
    showHeader() {
      return (
        (this.product.banner.mainElement.title ||
          this.product.banner.mainElement.subtitle) &&
        !this.minimal
      );
    },
    showSlider() {
      if (this.minimal || !this.productSliderIds.length) return false;
      // Mobile renders directly from the initial payload (no fetch)
      if (this.isBreakpointMobile) return true;
      // Desktop: hide the slider when the lazy-load resolved with no usable
      // products (network error or all offline) instead of showing skeletons.
      if (isSliderBannerUnavailable(this.product.banner)) return false;
      if (this.loadedProducts !== null) return this.loadedProducts.length > 0;
      return true;
    },
    /**
     * The slider products once enriched in place with full tile data (they then
     * carry variationAttributes), or null while still lightweight.
     * @returns {Object[]|null} enriched slider tiles, or null
     */
    loadedProducts() {
      return isSliderBannerEnriched(this.product.banner)
        ? this.product.banner.productSlider
        : null;
    },
    /**
     * Items fed to BaseCarousel:
     * - mobile: the minimal payload mapped to tile shape (rendered directly)
     * - desktop: full tiles after fetch, minimal payload as skeleton meanwhile
     * @returns {Object[]} items for the carousel
     */
    sliderItems() {
      if (this.isBreakpointMobile) return this.mobileSliderItems;
      return this.loadedProducts !== null
        ? this.loadedProducts
        : this.productSliderIds;
    },
  },

  watch: {
    // If the viewport crosses to desktop after the banner was already seen on
    // mobile (where we skip the fetch), load the full tiles lazily.
    isBreakpointDesktop(isDesktop) {
      if (
        isDesktop &&
        this.hasBeenVisible &&
        this.loadedProducts === null &&
        this.productSliderIds.length
      ) {
        this.lazyLoadSliderBannerProducts();
      }
    },
  },

  mounted() {
    if (this.mainElementVideo) {
      const { userAgent } = navigator;
      const isWindows = userAgent.includes('Windows');
      const isChromeOrEdge =
        userAgent.includes('Chrome') || userAgent.includes('Edg/');
      if (isWindows && isChromeOrEdge) {
        this.videoBlurFix = true;
      }
    }
  },

  methods: {
    // Slider-banner helper exposed for the template (skeleton tile).
    toSliderSkeletonProduct,
    decodeHtml(html) {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    },
    handleScrollIntoView(isVisible) {
      this.handleVisibilityChanged(isVisible);
      if (isVisible) {
        this.hasBeenVisible = true;
        const analytics = BANNER_TYPE_TO_ANALYTICS[this.bannerType]?.viewed;
        const url = this.product.banner.mainElement?.link?.url;
        if (analytics) {
          const { eventName, payloadType: type } = analytics;
          analyticsEventBus.publish(eventName, {
            position: this.gridIndex,
            type,
            url,
          });
        }
        // Only desktop lazy-fetches the full tile; mobile renders directly.
        if (this.isBreakpointDesktop && this.productSliderIds.length) {
          this.lazyLoadSliderBannerProducts();
        }
      }
    },
    /**
     * Lazy-loads the full slider tile data into the store (desktop). The store
     * action is idempotent and stores onto `banner.loadedProducts`; mobile keeps
     * rendering the lightweight `productSlider`. On desktop the tile renders as a
     * normal grid tile (SF-11727/SF-11428) with the product's own name/images.
     */
    lazyLoadSliderBannerProducts() {
      this.$store.dispatch(types.productList.loadSliderBannerProducts, {
        banner: this.product.banner,
      });
    },
    mainElementClick() {
      const url = this.product.banner.mainElement?.link?.url;
      if (url) {
        const analytics = BANNER_TYPE_TO_ANALYTICS[this.bannerType]?.clicked;
        if (analytics) {
          const { eventName, payloadType: type } = analytics;
          analyticsEventBus.publish(eventName, {
            url,
            type,
          });
        }
      }
    },
    getSizes() {
      return this.bannerType === bannerTypes.plpSimpleImage
        ? SIZES.PLP_GRID
        : SIZES.PLP_DOUBLE_WIDTH;
    },
  },
};
</script>
